import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { musicalWorkService } from "../services/musicalWorkService";
import { musicalWorkDisplayId } from "./MusicalWorkCommonStore";
import { MusicalWork } from "../../../api/graphql";
import { musicalGenreDisplayId } from "../../musicalGenre/store/MusicalGenreCommonStore";
import { getWorkAuthorValues } from "../../workAuthor/store/WorkAuthorCommonStore";
import { ColumnWidthInfo } from "../../common/services/crudServices";
import { interpreterDisplayId } from "../../interpreter/store/InterpreterCommonStore";
import { territoryDisplayId } from "../../territory/store/TerritoryCommonStore";
import { getPhonogramValues } from "../../phonogram/store/PhonogramCommonStore";
import {
  downloadWithAuth,
  fetchFileBlob,
} from "../../../common/utils/downloadWithAuth";
import { CrudRows } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { musicalGenreService } from "../../musicalGenre/services/musicalGenreService";
import { interpreterService } from "../../interpreter/services/interpreterService";
import { territoryService } from "../../territory/services/territoryService";
import { InitializeParams } from "../../../store/types";

export class MusicalWorkViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = musicalWorkService;
  musicalWorkInstance: MusicalWork | null = null;
  cardState: Record<string, boolean> = {
    workInfo: true,
    workRegistration: true,
    workContent: true,
    contracts: true,
    systemDates: false,
  };
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  titleValue: string = "";
  musicalGenreService = musicalGenreService;
  musicalGenreSearchValue: string = "";
  musicalGenreIdValue: string = "";
  musicalGenreSelectedValue: { displayId: string; id: string } | null = null;
  alternativeTitlesValue: string[] = [];
  authorsColumnWidths: Record<string, number | string> = {};
  authorsRows: CrudRows = {};
  notesValue: string = "";
  performersService = interpreterService;
  performersSearchValue: string = "";
  performersIdValue: string = "";
  performersSelectedValues: { displayId: string; id: string }[] = [];
  cmsIdValue: string = "";
  territoriesService = territoryService;
  territoriesSearchValue: string = "";
  territoriesIdValue: string = "";
  territoriesSelectedValues: { displayId: string; id: string }[] = [];
  iswcValue: string = "";
  workNumberValue: number = 0;
  scoreFiles: CrudRows = {};
  phonogramsColumnWidths: Record<string, number | string> = {};
  phonogramsRows: CrudRows = {};
  originalLyricsValue: string = "";
  contractObservationValue: string = "";
  createdAt_Value: Date | null = null;
  lastUpdatedAt_Value: Date | null = null;
  pdfViewerOpen: boolean = false;
  pdfViewerUrl: string = "";
  pdfViewerTitle: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;
    this.authorsColumnWidths = {
      __author: 200,
      lyricsPercentage: 200,
      musicPercentage: 200,
    };
    this.phonogramsColumnWidths = {
      audioFile: 200,
      trackName: 200,
      isrc: 200,
      albumName: 200,
      publicationYear: 200,
    };

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadMusicalWorkById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.musicalWorkOne({ id });
    this.musicalWorkInstance = result.musicalWorkOne;
    if (this.musicalWorkInstance) {
      this.loadValues(this.musicalWorkInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.musicalWorkInstance) {
      return musicalWorkDisplayId(this.musicalWorkInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.MusicalWorkSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "musicalGenre") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.musicalGenreOne({
          id: params.returnData.resultId,
        });
        const musicalGenre = result.musicalGenreOne;
        this.musicalGenreSelectedValue = musicalGenre;
      } else if (params.callerData?.fieldName === "performers") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.interpreterOne({
          id: params.returnData.resultId,
        });
        const interpreter = result.interpreterOne;
        this.performersSelectedValues.push(interpreter);
      } else if (params.callerData?.fieldName === "territories") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.territoryOne({
          id: params.returnData.resultId,
        });
        const territory = result.territoryOne;
        this.territoriesSelectedValues.push(territory);
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get musicalGenreValue() {
    if (this.musicalGenreSelectedValue) {
      return {
        id: this.musicalGenreSelectedValue.id,
        displayId: musicalGenreDisplayId(
          this.musicalGenreSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  get authorsVisibleColumns() {
    return [
      {
        name: "__author",
        title: this.langStore.WorkAuthorAuthorLabel,
        width: this.authorsColumnWidths["__author"],
      },
      {
        name: "lyricsPercentage",
        title: this.langStore.WorkAuthorLyricsPercentageLabel,
        width: this.authorsColumnWidths["lyricsPercentage"],
      },
      {
        name: "musicPercentage",
        title: this.langStore.WorkAuthorMusicPercentageLabel,
        width: this.authorsColumnWidths["musicPercentage"],
      },
    ];
  }

  authorsSetColumnWiths(x: ColumnWidthInfo[]) {
    const values: Record<string, number | string> = {};
    for (const item of x) {
      values[item.columnName] = item.width;
    }
    this.authorsColumnWidths = values;
  }

  get authorsRowsDyn() {
    const entries = Object.entries(this.authorsRows).map(([key, value]) => [
      key,
      {
        ...value,
        __author: this.langStore.displayIdByLang(value.__author),
      },
    ]);
    return Object.fromEntries(entries);
  }

  get performersValues() {
    return this.performersSelectedValues.map((x) => {
      return {
        id: x.id,
        displayId: interpreterDisplayId(x, this.langStore),
      };
    });
  }

  get territoriesValues() {
    return this.territoriesSelectedValues.map((x) => {
      return {
        id: x.id,
        displayId: territoryDisplayId(x, this.langStore),
      };
    });
  }

  get phonogramsVisibleColumns() {
    return [
      {
        name: "audioFile",
        title: this.langStore.PhonogramAudioFileLabel,
        width: this.phonogramsColumnWidths["audioFile"],
      },
      {
        name: "trackName",
        title: this.langStore.PhonogramTrackNameLabel,
        width: this.phonogramsColumnWidths["trackName"],
      },
      {
        name: "isrc",
        title: this.langStore.PhonogramIsrcLabel,
        width: this.phonogramsColumnWidths["isrc"],
      },
      {
        name: "albumName",
        title: this.langStore.PhonogramAlbumNameLabel,
        width: this.phonogramsColumnWidths["albumName"],
      },
      {
        name: "publicationYear",
        title: this.langStore.PhonogramPublicationYearLabel,
        width: this.phonogramsColumnWidths["publicationYear"],
      },
    ];
  }

  phonogramsSetColumnWiths(x: ColumnWidthInfo[]) {
    const values: Record<string, number | string> = {};
    for (const item of x) {
      values[item.columnName] = item.width;
    }
    this.phonogramsColumnWidths = values;
  }

  get phonogramsRowsDyn() {
    return this.phonogramsRows;
  }

  *fileDownload(props: any, fieldName: string): any {
    yield downloadWithAuth({
      entityName: "MusicalWork",
      fieldName: fieldName,
      instanceId: this.musicalWorkInstance?.id!,
      fileName: props.name,
    });
  }

  openPdfViewer(url: string, title: string) {
    this.pdfViewerUrl = url;
    this.pdfViewerTitle = title;
    this.pdfViewerOpen = true;
  }

  closePdfViewer() {
    if (this.pdfViewerUrl) {
      window.URL.revokeObjectURL(this.pdfViewerUrl);
    }
    this.pdfViewerOpen = false;
    this.pdfViewerUrl = "";
    this.pdfViewerTitle = "";
  }

  *viewFilePdf(props: any, fieldName: string): any {
    try {
      const blob = yield fetchFileBlob({
        entityName: "MusicalWork",
        fieldName: fieldName,
        instanceId: this.musicalWorkInstance?.id!,
        fileName: props.name,
      });
      if (!blob) return;
      this.openPdfViewer(window.URL.createObjectURL(blob), props.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    }
  }

  // Datos de la obra con autores para el gráfico de participación
  get workWithAuthors(): any[] {
    if (!this.musicalWorkInstance) {
      return [];
    }
    return [this.musicalWorkInstance];
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.musicalWorkInstance = null;
    this.titleValue = "";
    this.musicalGenreSearchValue = "";
    this.musicalGenreIdValue = "";
    this.alternativeTitlesValue = [];
    this.authorsRows = {};
    this.notesValue = "";
    this.performersSearchValue = "";
    this.performersIdValue = "";
    this.performersSelectedValues = [];
    this.cmsIdValue = "";
    this.territoriesSearchValue = "";
    this.territoriesIdValue = "";
    this.territoriesSelectedValues = [];
    this.iswcValue = "";
    this.workNumberValue = 0;
    this.scoreFiles = {};
    this.phonogramsRows = {};
    this.originalLyricsValue = "";
    this.contractObservationValue = "";
    this.createdAt_Value = null;
    this.lastUpdatedAt_Value = null;
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelMusicalWork() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("musicalWork_update");
  }

  editMusicalWork() {
    const id = this.musicalWorkInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/musicalWork/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printMusicalWork(handlePrint: () => void): void {
    handlePrint();
  }

  *closeMusicalWork(): any {
    yield this.close();
  }

  loadValues(instance: MusicalWork) {
    this.idValue = instance.id;
    this.titleValue = instance.title;
    this.musicalGenreSelectedValue = instance.musicalGenre;
    this.alternativeTitlesValue = instance.alternativeTitles || [];
    this.authorsRows = instance.authors
      ? Object.assign(
          {},
          ...instance.authors.map((x) => {
            const processedRow = getWorkAuthorValues(x, this.langStore, false);
            return {
              [`__${x.id}`]: { ...processedRow, __status__: "original" },
            };
          })
        )
      : {};

    this.notesValue = instance.notes || "";
    this.performersSelectedValues = instance.performers
      ? instance.performers
      : [];
    this.cmsIdValue = instance.cmsId || "";
    this.territoriesSelectedValues = instance.territories
      ? instance.territories
      : [];
    this.iswcValue = instance.iswc || "";
    this.workNumberValue = instance.workNumber;
    this.scoreFiles = instance.score_data
      ? {
          [instance.score_data.name]: {
            name: instance.score_data.name,
            formattedSize: formatBytes(instance.score_data?.size || 0),
            mimetype: instance.score_data?.mimetype,
            id: instance.score_data.name,
            __status__: "original",
          },
        }
      : {};
    this.phonogramsRows = instance.phonograms
      ? Object.assign(
          {},
          ...instance.phonograms.map((x) => {
            const processedRow = getPhonogramValues(x, this.langStore, false);
            return {
              [`__${x.id}`]: { ...processedRow, __status__: "original" },
            };
          })
        )
      : {};

    this.originalLyricsValue = instance.originalLyrics || "";
    this.contractObservationValue = instance.contractObservation || "";
    this.createdAt_Value = new Date(instance.createdAt_);
    this.lastUpdatedAt_Value = new Date(instance.lastUpdatedAt_);
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadMusicalWorkById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
